import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import NotFound from './pages/NotFound';

// Agent — outil interne call center
import AgentLogin from './pages/agent/AgentLogin';
import AgentClients from './pages/agent/AgentClients';
import AgentSession from './pages/agent/AgentSession';
import AgentLeads from './pages/agent/AgentLeads';
import AgentHistory from './pages/agent/AgentHistory';

// Admin — fondateur + superviseurs (JWT)
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUtilisateurs from './pages/admin/AdminUtilisateurs';
import AdminAides from './pages/admin/AdminAides';
import AdminPaiements from './pages/admin/AdminPaiements';
import AdminLogs from './pages/admin/AdminLogs';
import AdminAudit from './pages/admin/AdminAudit';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import AdminClients from './pages/admin/AdminClients';
import AidesListPage from './pages/admin/aides/AidesListPage';
import AideEditPage from './pages/admin/aides/AideEditPage';
import AideHistoryPage from './pages/admin/aides/AideHistoryPage';

/**
 * RootRedirect — redirige depuis `/` selon l'auth résolue par AuthContext.
 * - admin → /admin/dashboard
 * - agent → /agent/clients
 * - sinon → /agent (page login, défaut outil interne)
 *
 * Sprint Frontend B3 : lit `useAuth()` (cookie httpOnly + /api/auth/me)
 * au lieu de `localStorage`.
 */
function RootRedirect() {
  const { status, role } = useAuth();
  if (status === 'loading') {
    return (
      <div style={{
        padding: 40, textAlign: 'center', fontFamily: 'system-ui, sans-serif',
        color: '#6B5847',
      }}>Chargement…</div>
    );
  }
  if (status === 'authenticated' && role === 'admin') {
    return <Navigate to="/admin/dashboard" replace />;
  }
  if (status === 'authenticated' && role === 'agent') {
    return <Navigate to="/agent/clients" replace />;
  }
  return <Navigate to="/agent" replace />;
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Root — redirect intelligent selon auth */}
            <Route path="/" element={<RootRedirect />} />

            {/* Agent — outil interne call center */}
            <Route path="/agent"             element={<AgentLogin />} />
            <Route path="/agent/clients"     element={<AgentClients />} />
            <Route path="/agent/clients/:id"          element={<AgentSession />} />
            <Route path="/agent/clients/:id/history"  element={<AgentHistory />} />
            <Route path="/agent/leads"                element={<AgentLeads />} />

            {/* Admin — protégé par JWT */}
            <Route path="/admin"                              element={<AdminLogin />} />
            <Route path="/admin/dashboard"                    element={<AdminDashboard />} />
            <Route path="/admin/utilisateurs"                 element={<AdminUtilisateurs />} />
            <Route path="/admin/clients"                      element={<AdminClients />} />
            <Route path="/admin/aides"                        element={<AdminAides />} />
            <Route path="/admin/aides/catalogue"              element={<AidesListPage />} />
            <Route path="/admin/aides/catalogue/new"          element={<AideEditPage />} />
            <Route path="/admin/aides/catalogue/:id"          element={<AideEditPage />} />
            <Route path="/admin/aides/catalogue/:id/history" element={<AideHistoryPage />} />
            <Route path="/admin/paiements"                    element={<AdminPaiements />} />
            <Route path="/admin/logs"                         element={<AdminLogs />} />
            <Route path="/admin/audit"                        element={<AdminAudit />} />
            <Route path="/admin/analytics"                    element={<AdminAnalytics />} />

            {/* Fallback 404 (B5) */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}
